import React, { useEffect, useMemo, useState } from 'react';
import Header from './Header';
import { apiService } from '../services/api';
import IndicationCombo from './tracker/IndicationCombo';
import AssetDrawer from './assets/AssetDrawer';
import { TrialTrackerIndication, groupByIndication, TrackerGroup } from '../utils/trialTrackerTimeline';

interface AssetIndicationRef {
  id: number;
  name: string;
}

interface AssetRow {
  id: number;
  asset_name: string;
  modality?: string | null;
  target?: string | null;
  pmids?: number[] | null;
  other_names?: string[] | null;
  company_id?: number | null;
  company_name?: string | null;
  trial_count: number;
  indications: AssetIndicationRef[];
}

// One (asset, indication) pair — an asset in development across multiple tumor types shows up
// once per indication group, same posture as the trial tracker's per-trial grouping.
interface AssetGroupItem extends AssetRow {
  indication_name: string;
}

const chipBase = 'text-xs font-medium px-2.5 py-1 rounded-full border cursor-pointer whitespace-nowrap transition-colors';

const AssetsView: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [modalityOptions, setModalityOptions] = useState<string[]>([]);
  const [indications, setIndications] = useState<TrialTrackerIndication[]>([]);

  const [query, setQuery] = useState('');
  const [modalityFilter, setModalityFilter] = useState('all');
  const [targetQuery, setTargetQuery] = useState('');
  const [indicationFilter, setIndicationFilter] = useState('all');
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [assetsRes, indicationsRes] = await Promise.all([
          apiService.fetchAssets({ limit: 20000 }),
          apiService.fetchTrialTrackerIndications(),
        ]);
        if (cancelled) return;
        if (!assetsRes?.success) {
          setError(assetsRes?.error || 'Failed to load assets.');
          setAssets([]);
        } else {
          setAssets(assetsRes.data || []);
          setModalityOptions(assetsRes.options?.modalities || []);
        }
        setIndications(indicationsRes?.indications || []);
      } catch {
        if (!cancelled) {
          setError('Failed to load assets.');
          setAssets([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const filteredAssets = useMemo(() => {
    const q = query.trim().toLowerCase();
    const tq = targetQuery.trim().toLowerCase();
    return assets.filter((a) => {
      if (modalityFilter !== 'all' && (a.modality || '') !== modalityFilter) return false;
      if (tq && !(a.target || '').toLowerCase().includes(tq)) return false;
      if (indicationFilter !== 'all' && !a.indications.some((i) => String(i.id) === indicationFilter)) return false;
      if (q) {
        const haystack = [a.asset_name, a.target, a.modality, a.company_name, ...(a.other_names || [])]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [assets, query, modalityFilter, targetQuery, indicationFilter]);

  const showGroupHeaders = indicationFilter === 'all';

  const groups: TrackerGroup<AssetGroupItem>[] = useMemo(() => {
    const exploded: AssetGroupItem[] = [];
    filteredAssets.forEach((a) => {
      if (a.indications.length === 0) {
        exploded.push({ ...a, indication_name: 'Other / unspecified' });
      } else {
        a.indications.forEach((ind) => exploded.push({ ...a, indication_name: ind.name }));
      }
    });
    return groupByIndication(exploded);
  }, [filteredAssets]);

  const resultCount = filteredAssets.length;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <div className="flex-1 flex min-h-0 h-[calc(100vh-5rem)]">
        {/* Sidebar */}
        <div className="w-64 flex-none bg-slate-50 border-r border-slate-200 flex flex-col h-full overflow-y-auto">
          <div className="flex-1 min-w-0 overflow-y-auto p-3.5">
            <div className="text-[10.5px] font-bold tracking-wide text-slate-400 mb-2 px-0.5">SEARCH</div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search assets, target, company…"
              className="w-full text-[11.5px] text-slate-700 bg-white border border-slate-200 rounded-md px-2.5 py-1.5 mb-3.5 outline-none focus:border-indigo-400"
            />

            <div className="text-[10.5px] font-bold tracking-wide text-slate-400 mb-2 px-0.5">INDICATION</div>
            <IndicationCombo indications={indications} value={indicationFilter} onChange={setIndicationFilter} />

            <div className="text-[10.5px] font-bold tracking-wide text-slate-400 mb-2 px-0.5">MODALITY</div>
            <div className="flex flex-wrap gap-1.5 mb-3.5">
              {['all', ...modalityOptions].map((m) => (
                <span
                  key={m}
                  onClick={() => setModalityFilter(m)}
                  className={`${chipBase} ${modalityFilter === m ? 'bg-indigo-600 border-indigo-600 text-white font-semibold' : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'}`}
                >
                  {m === 'all' ? 'All' : m}
                </span>
              ))}
            </div>

            <div className="text-[10.5px] font-bold tracking-wide text-slate-400 mb-2 px-0.5">TARGET</div>
            <input
              value={targetQuery}
              onChange={(e) => setTargetQuery(e.target.value)}
              placeholder="e.g. KRAS G12C, HER2…"
              className="w-full text-[11.5px] text-slate-700 bg-white border border-slate-200 rounded-md px-2.5 py-1.5 outline-none focus:border-indigo-400"
            />
          </div>
        </div>

        {/* Main list */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-none px-6 pt-4 pb-3 border-b border-slate-200 bg-white flex items-center gap-2.5">
            <h1 className="font-serif text-lg font-bold text-slate-900">Assets</h1>
            <span className="text-[11.5px] text-slate-400">{resultCount} asset{resultCount === 1 ? '' : 's'}</span>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-500">{error}</div>
          ) : (
            <div className="flex-1 overflow-y-auto p-5">
              {resultCount === 0 ? (
                <div className="py-16 text-center">
                  <div className="text-sm font-semibold text-slate-600">No assets match this search</div>
                  <div className="text-xs text-slate-400 mt-1">Try a different name, target, or modality — or clear the filters.</div>
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {groups.map((group) => (
                    <React.Fragment key={group.name}>
                      {showGroupHeaders && (
                        <div className="flex items-center gap-2 pt-1 first:pt-0">
                          <span className="text-[11px] font-bold text-slate-500">{group.name}</span>
                          <span className="text-[10px] text-slate-400">{group.items.length}</span>
                          <div className="flex-1 h-px bg-slate-200" />
                        </div>
                      )}
                      {group.items.map((a, i) => (
                        <div
                          key={`${a.id}-${a.indication_name}-${i}`}
                          onClick={() => setSelectedAssetId(a.id)}
                          className="bg-white border border-slate-200 rounded-xl px-3.5 py-3 cursor-pointer hover:border-indigo-300 transition-colors"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[14.5px] font-semibold text-indigo-700">{a.asset_name}</span>
                            {a.target && (
                              <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{a.target}</span>
                            )}
                            {a.modality && (
                              <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">{a.modality}</span>
                            )}
                            <span className="ml-auto text-[10.5px] text-slate-400">{a.company_name || '—'}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 text-[11.5px] text-slate-400 flex-wrap">
                            <span>{a.trial_count} emerging trial{a.trial_count === 1 ? '' : 's'}</span>
                            {a.other_names && a.other_names.length > 0 && (
                              <>
                                <span>·</span>
                                <span className="truncate">{a.other_names.join(', ')}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {selectedAssetId && (
          <AssetDrawer assetId={selectedAssetId} onClose={() => setSelectedAssetId(null)} />
        )}
      </div>
    </div>
  );
};

export default AssetsView;
